import { useMemo, useState } from 'react';
import { buildItems, itemStats, minToTime } from './calendarLayout';
import { CAL_TYPES, CAL_TYPE_ORDER, HEAT_RAMP, calType, heatLevel, type CalSession } from './calendarTypes';

/* Week pulse — a day × hour heat grid.
 *
 * Built for scanning, not reading: the cell shading says how busy an hour is,
 * and each session is a 10×6 pill (filled = full, hollow = still has room).
 * Anything you actually need to read lives in the tooltip or behind a click
 * into the full timeline. */

export type HeatMetric = 'fill' | 'bookings' | 'sessions';

export interface PulseDay {
  /** 0–6, Monday first. */
  i: number;
  dow: string;
  date: number;
  mon: string;
  dateStr: string;
  today: boolean;
}

interface HourCell {
  list: CalSession[];
  booked: number;
  cap: number;
  full: number;
  wait: number;
}

/** Group the week's sessions by (day, start hour). Cancelled sessions are left
 *  out entirely — they are not load on the gym. */
function hourCells(sessions: CalSession[]): Record<string, HourCell> {
  const g: Record<string, HourCell> = {};
  for (const s of sessions) {
    if (s.status === 'cancelled' || s.day < 0) continue;
    const key = `${s.day}-${Math.floor(s.start / 60)}`;
    const c = (g[key] ||= { list: [], booked: 0, cap: 0, full: 0, wait: 0 });
    c.list.push(s);
    c.booked += s.booked;
    c.cap += s.cap;
    c.wait += s.wait;
    if (s.booked >= s.cap) c.full++;
  }
  for (const c of Object.values(g)) {
    c.list.sort(
      (a, b) => CAL_TYPE_ORDER.indexOf(a.type) - CAL_TYPE_ORDER.indexOf(b.type) || a.start - b.start
    );
  }
  return g;
}

const MAX_PILLS = 14;

export function WeekPulse({ sessions, days, metric, hours, now, onOpen }: {
  sessions: CalSession[];
  days: PulseDay[];
  metric: HeatMetric;
  /** Inclusive start hour, exclusive end hour. */
  hours: [number, number];
  /** Gym-local now; `min` drives the marker, `dateStr` decides the column. */
  now: { dateStr: string; min: number };
  onOpen: (init: { mode: 'week' | 'day'; day?: number; hour?: number }) => void;
}) {
  const [tip, setTip] = useState<{ c: HourCell; d: PulseDay; h: number; rect: DOMRect } | null>(null);

  const cells = useMemo(() => hourCells(sessions), [sessions]);

  /** Week-wide extremes, so the ramp uses its whole range on this week's data. */
  const range = useMemo(() => {
    let maxBooked = 1;
    let maxCount = 1;
    let fillMin = 1;
    let fillMax = 0;
    for (const c of Object.values(cells)) {
      maxBooked = Math.max(maxBooked, c.booked);
      maxCount = Math.max(maxCount, c.list.length);
      if (c.cap > 0) {
        const f = c.booked / c.cap;
        fillMin = Math.min(fillMin, f);
        fillMax = Math.max(fillMax, f);
      }
    }
    return { maxBooked, maxCount, fillMin, fillMax };
  }, [cells]);

  /* Fill % is min–max normalised across the week on purpose. A gym running at
     70–95% everywhere would otherwise be one flat wall of colour; what an admin
     wants to see is which hours are the quiet ones relative to the rest. */
  const valueOf = (c: HourCell | undefined): number => {
    if (!c) return 0;
    if (metric === 'bookings') return c.booked / range.maxBooked;
    if (metric === 'sessions') return c.list.length / range.maxCount;
    if (!c.cap) return 0;
    return Math.max(0.01, (c.booked / c.cap - range.fillMin) / Math.max(0.01, range.fillMax - range.fillMin));
  };

  const dayStats = useMemo(
    () =>
      days.map(d => {
        const list = sessions.filter(s => s.day === d.i && s.status !== 'cancelled');
        const booked = list.reduce((a, s) => a + s.booked, 0);
        const cap = list.reduce((a, s) => a + s.cap, 0);
        return { n: list.length, fill: cap ? Math.round((booked / cap) * 100) : 0 };
      }),
    [sessions, days]
  );

  const hourList = useMemo(() => {
    const out: number[] = [];
    for (let h = hours[0]; h < hours[1]; h++) out.push(h);
    return out;
  }, [hours]);

  const nowHour = Math.floor(now.min / 60);
  const nowFrac = (now.min % 60) / 60;

  return (
    <div className="a-pulse" onMouseLeave={() => setTip(null)}>
      <div className="a-pg">
        <div />
        {days.map((d, i) => (
          <button
            key={d.i}
            className={`a-pd${d.today ? ' today' : ''}`}
            onClick={() => onOpen({ mode: 'day', day: d.i })}
            title={`Open ${d.dow} ${d.date} by resource`}
            aria-label={`${d.dow} ${d.date} ${d.mon}, ${dayStats[i].n} sessions, ${dayStats[i].fill}% booked. Open by resource`}
          >
            <span className="a-pd-dow">{d.dow}</span>
            <span className="a-pd-date">{d.date}</span>
            <span className="a-pd-stat">{dayStats[i].n} <em>·</em> {dayStats[i].fill}%</span>
          </button>
        ))}

        {hourList.map(h => (
          <FragmentRow
            key={h}
            hour={h}
            days={days}
            cells={cells}
            valueOf={valueOf}
            nowHour={nowHour}
            nowFrac={nowFrac}
            onOpen={onOpen}
            onTip={setTip}
          />
        ))}
      </div>
      {tip && <PulseTooltip {...tip} />}
    </div>
  );
}

function FragmentRow({ hour, days, cells, valueOf, nowHour, nowFrac, onOpen, onTip }: {
  hour: number;
  days: PulseDay[];
  cells: Record<string, HourCell>;
  valueOf: (c: HourCell | undefined) => number;
  nowHour: number;
  nowFrac: number;
  onOpen: (init: { mode: 'week' | 'day'; day?: number; hour?: number }) => void;
  onTip: (t: { c: HourCell; d: PulseDay; h: number; rect: DOMRect } | null) => void;
}) {
  return (
    <>
      <div className="a-ptime">{String(hour).padStart(2, '0')}:00</div>
      {days.map(d => {
        const c = cells[`${d.i}-${hour}`];
        const level = c ? heatLevel(valueOf(c)) : 0;
        const allPast = !!c && c.list.every(s => s.past);
        const extra = c ? c.list.length - MAX_PILLS : 0;
        const pct = c && c.cap ? Math.round((c.booked / c.cap) * 100) : 0;
        return (
          <button
            key={d.i}
            className={`a-pc${allPast ? ' past' : ''}${c ? '' : ' empty'}`}
            style={{ background: HEAT_RAMP[level] }}
            onMouseEnter={e => c && onTip({ c, d, h: hour, rect: e.currentTarget.getBoundingClientRect() })}
            onMouseLeave={() => onTip(null)}
            onFocus={e => c && onTip({ c, d, h: hour, rect: e.currentTarget.getBoundingClientRect() })}
            onBlur={() => onTip(null)}
            onClick={() => onOpen({ mode: 'week', day: d.i, hour })}
            aria-label={
              c
                ? `${d.dow} ${d.date}, ${String(hour).padStart(2, '0')}:00, ${c.list.length} sessions, ${pct}% booked`
                : `${d.dow} ${d.date}, ${String(hour).padStart(2, '0')}:00, nothing scheduled`
            }
          >
            {c && (
              <span className="a-pills">
                {c.list.slice(0, MAX_PILLS).map(s => {
                  const T = calType(s.type);
                  const full = s.booked >= s.cap;
                  return <i key={s.id} style={{ background: full ? T.c : '#fff', borderColor: T.c }} />;
                })}
                {extra > 0 && <b>+{extra}</b>}
              </span>
            )}
            {d.today && hour === nowHour && <span className="a-pnow" style={{ top: `${nowFrac * 100}%` }} />}
          </button>
        );
      })}
    </>
  );
}

const TIP_W = 270;

function PulseTooltip({ c, d, h, rect }: { c: HourCell; d: PulseDay; h: number; rect: DOMRect }) {
  // Sits to the right of the cell, flipping left when that would run off-screen.
  const flip = rect.right + TIP_W + 18 > window.innerWidth;
  const left = flip ? Math.max(8, rect.left - TIP_W - 8) : rect.right + 8;
  const top = Math.max(8, Math.min(window.innerHeight - 240, rect.top - 6));
  const pct = c.cap ? Math.round((c.booked / c.cap) * 100) : 0;

  return (
    <div className="acal-tip" style={{ left, top }}>
      <div className="h">
        {d.dow} {d.date} · {String(h).padStart(2, '0')}:00–{String(h + 1).padStart(2, '0')}:00
      </div>
      <div className="s">
        <b>{c.list.length}</b> sessions · <b>{c.booked}</b>/{c.cap} booked · <b>{pct}%</b>
      </div>
      {(c.full > 0 || c.wait > 0) && (
        <div className="s">
          {c.full} full{c.wait > 0 ? ` · ${c.wait} on waitlist` : ''}
        </div>
      )}
      <div className="l">
        {c.list.slice(0, 6).map(s => (
          <div key={s.id}>
            <i style={{ background: calType(s.type).c }} />
            <span>{minToTime(s.start)}</span>
            <span className="nm">{s.slot ? s.res : s.name}</span>
            <span className="fl">{s.cap === 1 ? (s.booked ? 'Booked' : 'Open') : `${s.booked}/${s.cap}`}</span>
          </div>
        ))}
        {c.list.length > 6 && <div className="more">+{c.list.length - 6} more — click to open</div>}
      </div>
    </div>
  );
}

// ─── List view ───────────────────────────────────────────────────────────────

/** The alternative to the pulse: every session, grouped by day, readable.
 *  Slot runs collapse to one row — "Badminton · Court 1 · 06:00–10:00 · 4 slots". */
export function CalendarList({ sessions, days, onPick }: {
  sessions: CalSession[];
  days: PulseDay[];
  onPick: (s: CalSession) => void;
}) {
  const items = useMemo(() => buildItems(sessions), [sessions]);

  const anything = items.length > 0;
  if (!anything) {
    return <div className="a-empty">Nothing scheduled this week with the current filters.</div>;
  }

  return (
    <div className="a-lv">
      {days.map(d => {
        const list = items
          .filter(i => i.kind !== 'more' && i.s.day === d.i)
          .sort((a, b) => a.start - b.start);
        if (!list.length) return null;
        return (
          <section key={d.i} className="a-lv-day">
            <h3 className={d.today ? 'today' : ''}>
              {d.dow} {d.date} {d.mon}
              <span>{list.length} item{list.length === 1 ? '' : 's'}</span>
            </h3>
            {list.map(it => {
              if (it.kind === 'more') return null;
              const T = CAL_TYPES[it.type];
              const st = itemStats(it);
              const s = it.s;
              /* A 1:1 bookable reads as the member who holds it; the resource
                 is just the trainer's name again, so it is left out. */
              const sub =
                it.kind === 'run'
                  ? `${it.slots.length} slots`
                  : s.cap === 1
                    ? s.member || [s.res, s.who].filter(Boolean).join(' · ')
                    : [s.res, s.who].filter(Boolean).join(' · ');
              return (
                <button
                  key={it.id}
                  className={`a-lv-r${s.past ? ' past' : ''}${s.status === 'cancelled' ? ' cx' : ''}`}
                  onClick={() => onPick(s)}
                >
                  <span className="a-lv-t">{minToTime(it.start)}–{minToTime(it.end)}</span>
                  <i style={{ background: T.c }} />
                  <span className="a-lv-n">
                    <span>{it.kind === 'run' ? `${s.name} · ${s.res}` : s.name}</span>
                    <em>{sub}</em>
                  </span>
                  <span className="a-lv-f">
                    <span className="a-fill">
                      <span style={{ width: `${st.fill * 100}%`, background: T.c }} />
                    </span>
                    {st.booked}/{st.cap}
                  </span>
                  <span className="a-lv-s">
                    {/* "Full" on a capacity of one is noise — a booked PT hour
                        is not a problem. Only multi-capacity items say it. */}
                    {s.status === 'cancelled'
                      ? 'Cancelled'
                      : st.wait
                        ? `+${st.wait} waitlist`
                        : st.cap > st.n && st.n > 0 && st.full === st.n
                          ? 'Full'
                          : ''}
                  </span>
                </button>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
